import React, {useCallback, useEffect, useRef, useState} from "react";
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import {useTranslate} from "../../../system/useTranslate";

export const SCOPES = ["this", "following", "all"];

/**
 * Asks which occurrences of a recurring task a change applies to (this / this and following / all).
 * mode: "edit" | "rule" | "delete". `scopes` can restrict the offered choices.
 */
function RecurrenceScopeDialog({visible, mode = "edit", scopes = SCOPES, onSelect, onHide}) {
    const translate = useTranslate();
    const [selected, setSelected] = useState("this");

    useEffect(() => {
        if (visible) setSelected(scopes.includes("this") ? "this" : scopes[0]);
    }, [visible, scopes]);

    const isDelete = mode === "delete";

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header={translate(`recurrence.scope.title.${mode}`)}
            className="rtl w-11 sm:w-8 md:w-5 lg:w-4"
            pt={{root: () => ({className: "border-round-2xl"}), footer: () => ({className: "border-round-bottom-2xl"})}}
            footer={
                <div className="flex flex-row-reverse gap-2">
                    <Button
                        className={`text-xs ${isDelete ? "p-button-danger" : ""}`}
                        label={translate(isDelete ? "recurrence.scope.delete-confirm" : "recurrence.scope.confirm")}
                        onClick={() => onSelect?.(selected)}
                    />
                    <Button className="p-button-text text-xs" label={translate("cancel")} onClick={onHide}/>
                </div>
            }
        >
            <div className="flex flex-column gap-2" role="radiogroup" aria-label={translate(`recurrence.scope.title.${mode}`)}>
                {scopes.map((scope) => (
                    <label
                        key={scope}
                        className={`flex align-items-start gap-2 p-2 border-1 border-round-lg cursor-pointer ${selected === scope ? "border-primary surface-50" : "border-200"}`}
                    >
                        <input
                            type="radio"
                            name="recurrence-scope"
                            checked={selected === scope}
                            onChange={() => setSelected(scope)}
                        />
                        <span className="flex flex-column gap-1">
                            <span className="text-xs font-medium">{translate(`recurrence.scope.${scope}`)}</span>
                            <span className="text-2xs text-500">{translate(`recurrence.scope.${scope}-hint`)}</span>
                        </span>
                    </label>
                ))}
            </div>
        </Dialog>
    );
}

/**
 * const {askScope, scopeDialog} = useRecurrenceScope();
 * const scope = await askScope("edit");   // "this" | "following" | "all" | null (cancelled)
 * Render {scopeDialog} once in the component.
 */
export function useRecurrenceScope() {
    const [request, setRequest] = useState(null);
    const resolver = useRef(null);

    const finish = useCallback((scope) => {
        const resolve = resolver.current;
        resolver.current = null;
        setRequest(null);
        if (resolve) resolve(scope);
    }, []);

    const askScope = useCallback((mode = "edit", scopes = SCOPES) => new Promise((resolve) => {
        if (resolver.current) resolver.current(null);
        resolver.current = resolve;
        setRequest({mode, scopes});
    }), []);

    const scopeDialog = (
        <RecurrenceScopeDialog
            visible={Boolean(request)}
            mode={request?.mode}
            scopes={request?.scopes || SCOPES}
            onSelect={finish}
            onHide={() => finish(null)}
        />
    );

    return {askScope, scopeDialog};
}

export default RecurrenceScopeDialog;
